import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations from './translations';

const STORAGE_KEY = 'borabora-language';
const SUPPORTED_LANGUAGES = ['ja', 'zh', 'en'];
const LanguageContext = createContext(null);

const getTranslation = (language, path) => {
  const keys = path.split('.');
  const resolve = (source) => keys.reduce((value, key) => value?.[key], source);
  return resolve(translations[language]) ?? resolve(translations.ja) ?? path;
};

const getInitialLanguage = () => {
  try {
    const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(savedLanguage) ? savedLanguage : 'ja';
  } catch {
    return 'ja';
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language;
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // The selected language still works when browser storage is unavailable.
    }
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: (path) => getTranslation(language, path),
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }

  return context;
};
